import { useCallback, useState } from 'react';
import { createPortal, flushSync } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api.js';
import { money } from '../../lib/money.js';
import { tillDay } from '../../lib/till-day.js';
import { SignatureImage } from './signature-pad.js';

/**
 * The two papers a repair produces (SCOPE §11.1).
 *
 * **The claim ticket** goes home with the customer: the number, what was left,
 * what is being done, when it is promised. It is what they bring back, and the
 * number is printed large because it is read across a counter.
 *
 * **The signed waiver** is the evidence: the exact wording the customer agreed
 * to (kept with the signature, so a later edit to the shop's waiver never
 * changes it), who signed, when, and the signature itself drawn back from the
 * stored path. Printed the same way everything else in this product prints —
 * silently under kiosk printing, or to "Save as PDF" when a PDF is wanted.
 */

export type RepairDetail = Awaited<ReturnType<typeof api.repairs.get.query>>;

type Paper = { kind: 'ticket' | 'waiver'; detail: RepairDetail };

export function useRepairPrinter() {
  const [printing, setPrinting] = useState<Paper | null>(null);
  const settings = useQuery({
    queryKey: ['settings'],
    queryFn: () => api.settings.get.query(),
    staleTime: 60_000,
  });
  const store = settings.data?.store;

  const print = useCallback((paper: Paper) => {
    try {
      // Synchronously, before `window.print()` — React would otherwise batch
      // the render until after the dialog had captured a blank page.
      flushSync(() => setPrinting(paper));
      window.print();
    } finally {
      setPrinting(null);
    }
  }, []);

  const frame =
    printing === null
      ? null
      : createPortal(
          <div className="print-only">
            <article className="repair-doc">
              <header className="repair-doc__head">
                <div>
                  <div className="repair-doc__store">{store?.name ?? 'Sitzmark'}</div>
                  {store?.addressLine1 ? <div>{store.addressLine1}</div> : null}
                  {store?.city ? (
                    <div>
                      {store.city}
                      {store.region ? `, ${store.region}` : ''} {store.postalCode ?? ''}
                    </div>
                  ) : null}
                  {store?.phone ? <div>{store.phone}</div> : null}
                </div>
                <div className="repair-doc__title">
                  <div>{printing.kind === 'ticket' ? 'Repair ticket' : 'Service waiver'}</div>
                  <div className="repair-doc__number">{printing.detail.ticket.number}</div>
                </div>
              </header>
              {printing.kind === 'ticket' ? (
                <ClaimTicket detail={printing.detail} />
              ) : (
                <SignedWaiver detail={printing.detail} />
              )}
            </article>
          </div>,
          window.document.body,
        );

  return { print, frame };
}

function customerName(detail: RepairDetail): string {
  return [detail.customer.firstName, detail.customer.lastName].filter(Boolean).join(' ');
}

function ClaimTicket({ detail }: { detail: RepairDetail }) {
  return (
    <>
      <dl className="repair-doc__facts">
        <div>
          <dt>Customer</dt>
          <dd>
            {customerName(detail)}
            {detail.customer.phone ? ` · ${detail.customer.phone}` : ''}
          </dd>
        </div>
        <div>
          <dt>Equipment</dt>
          <dd>{detail.ticket.equipment}</dd>
        </div>
        <div>
          <dt>Promised</dt>
          <dd>
            {detail.ticket.promisedDate === null
              ? 'We will call when it is ready'
              : new Date(`${detail.ticket.promisedDate}T12:00:00`).toLocaleDateString(undefined, {
                  weekday: 'long',
                  month: 'long',
                  day: 'numeric',
                })}
          </dd>
        </div>
      </dl>
      <table className="repair-doc__lines">
        <tbody>
          {detail.lines.map((line) => (
            <tr key={line.id}>
              <td>
                {line.quantity > 1 ? `${line.quantity} × ` : ''}
                {line.description}
              </td>
              <td className="num">{money(line.quantity * line.unitPriceCents)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <th>Quoted, before tax</th>
            <th className="num">{money(detail.quoteCents)}</th>
          </tr>
        </tfoot>
      </table>
      {detail.ticket.notes ? <p className="repair-doc__notes">{detail.ticket.notes}</p> : null}
      <p className="repair-doc__notes">
        Bring this ticket when you collect your equipment. Taken in by{' '}
        {detail.ticket.intakeByName ?? 'the shop'} on{' '}
        {new Date(detail.ticket.createdAt).toLocaleDateString()}.
      </p>
    </>
  );
}

function SignedWaiver({ detail }: { detail: RepairDetail }) {
  const waiver = detail.waiver;
  return (
    <>
      <dl className="repair-doc__facts">
        <div>
          <dt>Customer</dt>
          <dd>{customerName(detail)}</dd>
        </div>
        <div>
          <dt>Equipment</dt>
          <dd>{detail.ticket.equipment}</dd>
        </div>
      </dl>
      {waiver === null ? (
        <p>No signed waiver is on file for this ticket.</p>
      ) : (
        <>
          <div className="repair-doc__waiver">{waiver.waiverText}</div>
          {waiver.signaturePath === null ? null : (
            <SignatureImage
              path={waiver.signaturePath}
              width={waiver.signatureWidth}
              height={waiver.signatureHeight}
              label={`Signature of ${waiver.signedName}`}
            />
          )}
          <p className="repair-doc__signed">
            Signed by {waiver.signedName} on {new Date(waiver.signedAt).toLocaleString()}
            {waiver.validUntil !== null && waiver.validUntil !== tillDay(new Date(waiver.signedAt))
              ? ` · covers work taken in until ${new Date(`${waiver.validUntil}T12:00:00`).toLocaleDateString()}`
              : ''}
          </p>
        </>
      )}
    </>
  );
}
